import { createTheme, PaletteMode } from '@mui/material';
import { enUS, esES, frFR, ptBR } from '@mui/material/locale';
import { alpha, darken, Palette, PaletteColor } from '@mui/material/styles';
import { SupportedLanguages } from '../i18n/translations';
import { getSemanticColorTokens } from './colorSemantics';
import { paletteBaseTokens } from './paletteTokens';
import { getSemanticTypographyTokens, typographyBaseTokens } from './typographyTokens';

const muiLocales: Record<SupportedLanguages, object[]> = {
  fr: [frFR],
  es: [esES],
  en: [enUS],
  pt: [ptBR],
};

const resolveLocale = (locale: string): SupportedLanguages =>
  locale in muiLocales ? (locale as SupportedLanguages) : 'es';

const resolvePaletteColor = (palette: Palette, color?: string): PaletteColor => {
  const key = (color || 'primary') as keyof Palette;
  const entry = palette[key];
  if (entry && typeof entry === 'object' && 'main' in entry) {
    return entry as PaletteColor;
  }
  return palette.primary;
};

const resolveButtonPaletteColor = (
  palette: Palette,
  mode: PaletteMode,
  color?: string
): PaletteColor => {
  // In dark mode, all action buttons should use the gold palette.
  if (mode === 'dark') {
    return resolvePaletteColor(palette, 'secondary');
  }

  return resolvePaletteColor(palette, color);
};

const createAppTheme = (mode: PaletteMode, locale: string) => {
  const semantic = getSemanticColorTokens(mode);
  const isDark = mode === 'dark';

  return createTheme(
    {
      palette: {
        mode,
        primary: semantic.palette.primary,
        secondary: semantic.palette.secondary,
        success: semantic.palette.success,
        error: semantic.palette.error,
        warning: semantic.palette.warning,
        info: semantic.palette.info,
        text: semantic.palette.text,
        background: semantic.palette.background,
        divider: semantic.palette.divider,
      },
      shape: {
        borderRadius: 14,
      },
      typography: getSemanticTypographyTokens(),
      components: {
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
              boxShadow: 'none',
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
            },
            rounded: {
              borderRadius: 18,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              overflow: 'hidden',
              border: `1px solid ${semantic.palette.divider}`,
              borderRadius: 22,
              backgroundImage: semantic.surfaces.cardGradient,
              boxShadow: isDark
                ? `0 18px 50px ${alpha(paletteBaseTokens.neutral[1000], 0.35)}`
                : `0 18px 50px ${alpha(semantic.palette.text.primary, 0.08)}`,
              transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
              '&:hover': {
                borderColor: alpha(semantic.palette.primary.main, 0.28),
                boxShadow: isDark
                  ? `0 22px 60px ${alpha(paletteBaseTokens.neutral[1000], 0.45)}`
                  : `0 22px 60px ${alpha(semantic.palette.text.primary, 0.12)}`,
                transform: 'translateY(-2px)',
              },
            },
          },
        },
        MuiButton: {
          defaultProps: {
            color: isDark ? 'secondary' : 'primary',
          },
          styleOverrides: {
            root: ({ theme }) => ({
              borderRadius: 999,
              boxShadow: 'none',
              textTransform: 'none',
              fontWeight: theme.typography.button?.fontWeight,
            }),
            contained: ({ theme, ownerState }) => {
              const palette = resolveButtonPaletteColor(
                theme.palette,
                theme.palette.mode,
                ownerState.color
              );
              const { main } = palette;

              return {
                backgroundColor: main,
                color: palette.contrastText,
                boxShadow: `0 10px 24px ${alpha(main, 0.24)}`,
                '&:hover': {
                  backgroundColor: darken(main, 0.12),
                  boxShadow: `0 14px 30px ${alpha(main, 0.3)}`,
                },
              };
            },
            outlined: ({ theme, ownerState }) => {
              const palette = resolveButtonPaletteColor(
                theme.palette,
                theme.palette.mode,
                ownerState.color
              );
              const { main } = palette;

              return {
                color: main,
                borderColor: alpha(main, 0.56),
                '&:hover': {
                  backgroundColor: alpha(main, 0.08),
                  borderColor: darken(main, 0.12),
                },
              };
            },
            text: ({ theme, ownerState }) => {
              const palette = resolveButtonPaletteColor(
                theme.palette,
                theme.palette.mode,
                ownerState.color
              );

              return {
                color: palette.main,
                '&:hover': {
                  backgroundColor: alpha(palette.main, 0.08),
                },
              };
            },
          },
        },
        MuiCheckbox: {
          defaultProps: {
            color: isDark ? 'secondary' : 'primary',
          },
        },
        MuiSwitch: {
          defaultProps: {
            color: isDark ? 'secondary' : 'primary',
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              transition: 'background-color 160ms ease, transform 160ms ease',
              '&:hover': {
                transform: 'translateY(-1px)',
              },
            },
          },
        },
        MuiListSubheader: {
          styleOverrides: {
            root: {
              ...getSemanticTypographyTokens().overline,
              backgroundColor: 'transparent',
              color: semantic.palette.text.secondary,
              lineHeight: typographyBaseTokens.lineHeights.spacious,
            },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              borderRadius: 14,
              marginInline: 8,
              marginBlock: 2,
              transition: 'background-color 160ms ease, color 160ms ease, transform 160ms ease',
              '&.Mui-selected': {
                backgroundColor: alpha(semantic.palette.primary.main, isDark ? 0.28 : 0.1),
                color: isDark ? semantic.palette.text.primary : semantic.palette.primary.dark,
                '& .MuiListItemIcon-root': {
                  color: isDark ? semantic.palette.secondary.main : semantic.palette.primary.main,
                },
              },
              '&.Mui-selected:hover, &:hover': {
                backgroundColor: alpha(semantic.palette.primary.main, isDark ? 0.22 : 0.08),
                transform: 'translateX(2px)',
              },
            },
          },
        },
        MuiListItemIcon: {
          styleOverrides: {
            root: {
              color: alpha(semantic.palette.text.primary, isDark ? 0.72 : 0.58),
              minWidth: 42,
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              backgroundImage: 'none',
              borderRadius: 22,
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: ({ theme }) => ({
              '&.Mui-focused': {
                color: theme.palette.primary.main,
              },
            }),
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: ({ theme }) => ({
              borderRadius: theme.shape.borderRadius,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: darken(theme.palette.primary.main, 0.08),
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
              },
            }),
          },
        },
        MuiInput: {
          styleOverrides: {
            root: ({ theme }) => ({
              '&:after': {
                borderBottomColor: theme.palette.primary.main,
              },
            }),
          },
        },
        MuiTableCell: {
          styleOverrides: {
            head: {
              fontWeight: typographyBaseTokens.fontWeights.extrabold,
            },
          },
        },
        MuiTabs: {
          styleOverrides: {
            indicator: ({ theme }) => ({
              height: 4,
              borderRadius: '4px 4px 0 0',
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.secondary.main
                  : theme.palette.primary.main,
            }),
          },
        },
        MuiTab: {
          styleOverrides: {
            root: ({ theme }) => {
              const isDarkMode = theme.palette.mode === 'dark';
              const accent = isDarkMode ? theme.palette.secondary.main : theme.palette.primary.main;

              return {
                textTransform: 'none',
                fontWeight: theme.typography.fontWeightMedium,
                color: isDarkMode
                  ? alpha(theme.palette.secondary.main, 0.78)
                  : theme.palette.text.secondary,
                transition:
                  'color 160ms ease, background-color 160ms ease, outline-color 160ms ease',
                '&:hover': {
                  color: accent,
                  backgroundColor: alpha(accent, isDarkMode ? 0.16 : 0.08),
                },
                '&.Mui-selected': {
                  color: accent,
                  fontWeight: theme.typography.fontWeightBold,
                },
                '&.Mui-focusVisible': {
                  color: accent,
                  outline: `2px solid ${alpha(accent, 0.58)}`,
                  outlineOffset: 2,
                  borderRadius: theme.shape.borderRadius,
                },
              };
            },
          },
        },
      },
      customTokens: {
        surfaces: semantic.surfaces,
        overlays: semantic.overlays,
      },
    },
    ...muiLocales[resolveLocale(locale)]
  );
};

export default createAppTheme;
